import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Divider,
  Box,
  Link,
  Zoom,
  CardActions,
  useTheme,
  Stack,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { formatAppointment, formatCurrency } from 'utils/formatter.js';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { FormattedData, SwimlaneBox, SwimlaneBoxDivider, OverflowTextTypography } from 'styles/GlobalStyle';
import StatusPill from 'components/StatusPill';
import { tokens } from 'theme/tokens';

const findKey = function(t) {
  if(!t) return '';
  if(!t.dataset.key) {
    return findKey(t.parentElement);
  }
  return t.dataset.key;
};

const DispatchGridView = ({
  orders,
  transportationMode,
  fnViewOrderHandler,
  fnEditOrderHandler,
  fnDeleteOrderHandler,
  fnOrderStatsHandler,  
  fnFileUploadHandler,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [elevation, setElevation] = React.useState(4);

  const getCurrencyColor = (amount) => {
    return amount > 0 ? colors.green[500] : colors.red[500];
  };
  const handleClick = (event, order) => {
    setSelectedOrder(order);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setSelectedOrder(null);
    setAnchorEl(null);
  };

  const renderOrderInformationInCard = (label, value, toolTip) => {
    return (
      <Stack sx={{px: 0.5}}>
        {toolTip ?
          <OverflowTextTypography
            variant={'body1'}
            fontWeight={600}
            color={colors.neutral[900]}
          >
            <Tooltip title={toolTip} arrow>{value}</Tooltip>
          </OverflowTextTypography>
        :
          <OverflowTextTypography
            variant={'body1'}
            fontWeight={600}
            color={colors.neutral[900]}
          >
            {value || '-'}
          </OverflowTextTypography>        
        }
        <Typography
          variant={'caption'}
          fontWeight={400}
          color={colors.neutral[500]}
          gutterBottom
        >
          {label}
        </Typography>                    
      </Stack>
    );
  };
  return (
    <>
      {orders.map(order => {
        let origins = [];
        let destinations = [];
        let containerNumber = 'None';
        let truckNumber = 'None';
        let trailerNumber = 'None';
        let driverName = 'Not Assigned';
        const equipmentTooltip = [];
        const driverTooltipData = [];

        {/* Order Information */ }
        const brokerName = order.broker.name;
        const brokerAddress = order.broker.address && `${order.broker.address.city}, ${order.broker.address.state}, ${order.broker.address.country}`;        
        const {lineHaul, fuelSurcharges, expenses, driverRate, legCharges, orderNet} = order.orderNet ? order.orderNet : 0;
        if(transportationMode === 'INTERMODAL') {
          containerNumber = order.container && order.container.deliveryContainerNumber;
          if (order.orderType === 'PICKUP') {
            containerNumber = order.container && order.container.pickupContainerNumber;
          } else if (order.orderType === 'DROP_PICK' || order.orderType === 'DROP') {
            equipmentTooltip.push(`Delivery Container #: ${order.container.deliveryContainerNumber}`);
            equipmentTooltip.push(<br />);
            equipmentTooltip.push(`Pickup Container #: ${order.container.pickupContainerNumber}`);
          }
        } else {
          truckNumber = order.truck && order.truck.truckNumber;
          
          equipmentTooltip.push(<Typography variant='body1'>Total Miles: {order.truck && order.truck.totalMiles}</Typography>);
          equipmentTooltip.push(<Typography variant='body1'>Insurance: {order.truck && order.truck.policyNumber}</Typography>);
          equipmentTooltip.push(<Typography variant='body1'>DOT Number: {order.truck && order.truck.dotNumber}</Typography>);          
        }
        const orderNetTooltipInformation = [];
        orderNetTooltipInformation.push(<Typography variant='body1'>LineHaul Rate: ${lineHaul}</Typography>);
        orderNetTooltipInformation.push(<Typography variant='body1'>FSC: ${fuelSurcharges}</Typography>);
        orderNetTooltipInformation.push(<Typography variant='body1'>Expenses (Billable): ${expenses}</Typography>);        
        orderNetTooltipInformation.push(<Typography variant='body1'>Driver Settlement: ${driverRate}</Typography>);
        orderNetTooltipInformation.push(<Typography variant='body1'>Charges: ${legCharges}</Typography>);

        {/* Order Leg Information */ }
        if (order.legs) {
          const legCount = order.legs.length;
          const lastLeg = order.legs[legCount - 1];
          origins = transportationMode === 'INTERMODAL' ? lastLeg.origins.filter(o => o.originalOrigin) : lastLeg.origins;
          destinations = transportationMode === 'INTERMODAL' ? lastLeg.destinations.filter(o => o.originalDestination) : lastLeg.destinations;
          
          const currentLeg = order.legs[0];
          driverName = currentLeg.drivers && currentLeg.drivers.length > 0 ? currentLeg.drivers.map(d => d.fullName).toString() : 'Not Assigned';
          order.legs.forEach(leg => {
            if(leg.drivers) {
              const driverInformation = leg.drivers.map(driver => `${driver.fullName} ${driver.phone}`);
              driverTooltipData.push(<Typography variant='body1'>{`Leg ${leg.id}: ${driverInformation}`}</Typography>);
            } else {
              driverTooltipData.push(<Typography variant='body1'>{`Leg ${leg.id}: No Driver Assigned`}</Typography>);
            }
          });
        }
        const documentLink = <Link component='button' underline='always' onClick={() => fnFileUploadHandler(order)}>{order.documents && order.documents.length}</Link>;
        return (
          <Card style={{height: '310px', marginBottom: '15px'}} data-key={order.id} variant='elevation' elevation={elevation == order.id ? 12 : 4} key={`key:order:${order.id}`}
            onMouseEnter={(evt)=> {
              evt.preventDefault();
              let id = findKey(evt.target);
              setElevation(id); 
            }}
            onMouseLeave={(evt)=>{ 
              evt.preventDefault();
              setElevation(-1); 
            }}
          >
            <CardHeader
              sx={{paddingX: 1, paddingY: 0}}
              title={
                <Box>
                  <Box display='flex' justifyContent='space-between'>
                    <Link variant='body1' component='button' underline='always' sx={{fontWeight: 600}}
                      onClick={() => fnViewOrderHandler(order)}>
                      {transportationMode === 'INTERMODAL' ? order.displayOrderId : order.loadId}
                    </Link>
                    <Typography variant='body1' sx={{fontWeight: 600}}>{order.orderType}</Typography>                 
                  </Box>
                  <Box display='flex' justifyContent='space-between'>
                    <Tooltip
                      TransitionComponent={Zoom}
                      placement='bottom'
                      title={orderNetTooltipInformation} arrow>
                      <FormattedData variant='body1'
                        sx={{fontWeight: 500, fontSmooth: 'auto', color: getCurrencyColor(orderNet)}}>
                        {formatCurrency(orderNet)}
                      </FormattedData>
                    </Tooltip>
                    <StatusPill
                      category={'payment'}
                      status={order.paymentStatus}
                      label={order.paymentStatus}
                      size='small'
                    />
                  </Box>
                </Box>
              }
              action={
                <Box display='flex'>
                  <IconButton
                    aria-label='settings'
                    onClick={(evt) => handleClick(evt, order)}
                    size="large">
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id='long-menu'
                    anchorEl={anchorEl}
                    keepMounted
                    open={open}
                    onClose={handleClose}
                  >
                    <MenuItem key={`edit:${order.id}`} onClick={() => fnEditOrderHandler(selectedOrder)}><EditOutlinedIcon fontSize='small' />&nbsp;&nbsp;Edit Order</MenuItem>
                    <Divider />
                    <MenuItem key={`delete:${order.id}`} onClick={() => fnDeleteOrderHandler(selectedOrder)}><DeleteOutlinedIcon fontSize='small' />&nbsp;&nbsp;Delete Order</MenuItem>                    
                    <Divider />                    
                    <MenuItem key={`files:${order.id}`} onClick={() => fnFileUploadHandler(selectedOrder)}><AttachFileIcon fontSize='small' />&nbsp;&nbsp;Upload Documents</MenuItem>
                    <Divider />
                    <MenuItem key={`stats:${order.id}`} onClick={() => fnOrderStatsHandler(selectedOrder)}><EqualizerOutlinedIcon fontSize='small' />&nbsp;&nbsp;Order Stats</MenuItem>
                    <Divider />
                  </Menu>
                </Box>
              }
            />
            <SwimlaneBox style={{padding: 0}}>
              <SwimlaneBoxDivider/>
              {/* ROW 2 - BROKER INFO */}

              <Grid container spacing={2}>
                <Grid size={{xs: 6, sm: 6}}>
                  {renderOrderInformationInCard('Broker', brokerName, <Typography variant='body1'>{brokerAddress}</Typography>)}
                </Grid>
                <Grid size={{xs: 6, sm: 6}}>
                  {renderOrderInformationInCard('Broker Load Number', order.brokerOrderNumber)}
                </Grid>
              </Grid>
              {/* ROW 3 - ORIGIN INFO */}
              {origins.map(origin =>
                <Grid container key={origin.id} spacing={2}>
                  <Grid size={{xs: 6, sm: 6}} key={`key:origin:${origin.id}`}>
                    {renderOrderInformationInCard('Origin', origin.name, <Typography variant='body1'>{origin.address.street}, {origin.address.city}, {origin.address.state}</Typography>)}                      
                  </Grid>
                  <Grid size={{xs: 6, sm: 6}} key={`key:origin2:${origin.id}`}>
                    <Stack>
                      <Typography
                        variant={'body1'}
                        fontWeight={600}
                        color={colors.neutral[900]}
                      >
                        {formatAppointment(origin.appointment, 'MM-DD-YYYY')}
                      </Typography>
                      <Typography
                        variant={'body1'}
                        fontWeight={600}
                        color={colors.neutral[500]}
                        gutterBottom
                      >
                        {formatAppointment(origin.appointment, 'h:mm A')}
                      </Typography>                    
                    </Stack>
                  </Grid>
                </Grid>
              )}
              {/* ROW 3 - DESTINATION INFO */}
              {destinations.map(destination =>
                <Grid container key={destination.id} spacing={2}>
                  <Grid size={{xs: 6, sm: 6}} key={`key:destination:${destination.id}`}>
                    {renderOrderInformationInCard('Destination', destination.name, <Typography variant='body1'>{destination.address.street}, {destination.address.city}, {destination.address.state}</Typography>)}
                  </Grid>
                  <Grid size={{xs: 6, sm: 6}} key={`key:destination2:${destination.id}`}>
                    <Stack>
                      <Typography
                        variant={'body1'}
                        fontWeight={600}
                        color={colors.neutral[900]}
                      >
                        {formatAppointment(destination.appointment, 'MM-DD-YYYY')}
                      </Typography>
                      <Typography
                        variant={'body1'}
                        fontWeight={600}
                        color={colors.neutral[500]}
                        gutterBottom
                      >
                        {formatAppointment(destination.appointment, 'h:mm A')}
                      </Typography>                    
                    </Stack>
                  </Grid>
                </Grid>
              )}
              {/* ROW 4 - Container or Truck & Driver */}
              <Grid container spacing={2}>
                <Grid size={{xs: 6, sm: 6}}>
                  {renderOrderInformationInCard('Driver', driverName, driverTooltipData)}
                </Grid>
                {transportationMode === 'INTERMODAL' ?
                  order.orderType === 'DROP_PICK' || order.orderType === 'DROP' ?
                    <Grid size={{xs: 6, sm: 6}}>
                      {renderOrderInformationInCard('Container', containerNumber, equipmentTooltip)}
                    </Grid> :
                    <Grid size={{xs: 6, sm: 6}}>
                      {renderOrderInformationInCard('Container', containerNumber)}
                    </Grid>: 
                  <Grid size={{xs: 6, sm: 6}}>
                    {renderOrderInformationInCard('Truck', truckNumber, equipmentTooltip)}
                  </Grid>
              }
              </Grid>
            </SwimlaneBox>
            <CardActions sx={{padding: 0}}>
              <Grid container spacing={2} >
                <Grid size={{xs: 6, sm: 6}}>
                  {renderOrderInformationInCard('Documents', documentLink)}
                </Grid>                
                <Grid size={{xs: 6, sm: 6}}>
                  {renderOrderInformationInCard('Notes', order.notes, order.notes)}
                </Grid>
              </Grid>              
            </CardActions>
          </Card>
        );
      })}
    </>
  );
};

DispatchGridView.propTypes = {
  className: PropTypes.string
};

export default DispatchGridView;